
import React, { useMemo } from "react";
import { Message } from "@/services/messages";
import { formatDate } from "@/utils/dateUtils";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Clock, ExternalLink } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface MessageItemProps {
  message: Message;
  isOwnMessage: boolean;
  otherUsername: string;
  isGrouped?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage,
  otherUsername,
  isGrouped = false
}) => {
  const navigate = useNavigate();
  
  // Check if message is still being sent (optimistic)
  const isOptimistic = message.id.startsWith('temp-');
  
  // Memoize values that won't change for better performance
  const formattedDate = useMemo(() => formatDate(message.created_at), [message.created_at]);
  
  // Check for action button
  const actionButton = message.payload?.actionButton;
  
  // Memoize card classes
  const cardClasses = useMemo(() => {
    return cn(
      "max-w-[85%] md:max-w-[70%] shadow-sm",
      isOwnMessage 
        ? `${isOptimistic ? "bg-primary/80" : "bg-primary"} text-primary-foreground rounded-2xl rounded-br-sm`
        : "bg-card hover:bg-secondary/50 transition-colors rounded-2xl rounded-bl-sm",
      isGrouped && "mt-1"
    );
  }, [isOwnMessage, isOptimistic, isGrouped]);
  
  return (
    <div 
      className={`flex ${isOwnMessage ? "justify-end" : "justify-start"} animate-fade-in`}
    >
      <Card className={cardClasses}>
        <CardContent className={cn("p-3", isGrouped && "pt-2 pb-2")}>
          <p>{message.content}</p>
          {actionButton && (
            <div className="mt-3">
              <Button
                size="sm"
                variant={actionButton.type === "primary" ? "default" : "outline"}
                onClick={() => navigate(actionButton.url)}
                className="flex items-center gap-2"
              >
                {actionButton.text}
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="px-3 py-1 text-xs text-muted-foreground flex justify-between">
          {!isGrouped && <span className="font-medium">{isOwnMessage ? "You" : otherUsername}</span>}
          <div className="flex items-center">
            {isOptimistic ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1 animate-pulse" />
                      {formattedDate}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Sending...</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="flex items-center">
                {formattedDate}
                {isOwnMessage && <Check className="h-3 w-3 ml-1" />}
              </span>
            )}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default React.memo(MessageItem);
